/**
 * Game UI Component
 * 
 * Complete game-style UI for game mode.
 * Designed to look like Lord of Ultima, Crown of the Gods, Tiberium Alliances.
 */

import { getImagePath } from '../utils/imagePath.js';

export class GameUI {
    constructor(containerElement, gameState, gameEngine, callbacks) {
        this.container = containerElement;
        this.gameState = gameState;
        this.gameEngine = gameEngine;
        this.callbacks = callbacks || {};
        
        // UI state
        this.selectedBuilding = null;
        this.updateInterval = null;
    }
    
    /**
     * Initialize the game UI
     */
    init() {
        this.render();
        this.attachEventListeners();
        this.startAutoUpdate();
    }
    
    /**
     * Render the complete game UI
     */
    render() {
        if (!this.container) return;
        
        const resources = this.gameState?.getResources() || {};
        const storage = this.gameState?.getStorage() || {};
        const cityName = this.gameState?.cityName || 'New City';
        
        this.container.innerHTML = `
            <!-- Top Resource Bar -->
            <div class="game-ui-topbar">
                <div class="game-ui-topbar-left">
                    <div class="game-ui-city-name">
                        <span class="game-ui-city-icon">🏰</span>
                        <span class="game-ui-city-text">${cityName}</span>
                    </div>
                    <div class="game-ui-player-info">
                        <span class="game-ui-player-level">Level 1</span>
                        <span class="game-ui-player-name">Player</span>
                    </div>
                </div>
                
                <div class="game-ui-resources">
                    <div class="game-ui-resource-item" data-resource="wood">
                        <img src="${getImagePath('ui/icon_resource_wood.png')}" alt="Wood" class="game-ui-resource-icon" onerror="this.style.display='none'">
                        <div class="game-ui-resource-content">
                            <span class="game-ui-resource-value">${this.formatNumber(resources.wood || 0)}</span>
                            <span class="game-ui-resource-storage">/ ${this.formatNumber(storage.wood || 0)}</span>
                        </div>
                    </div>
                    <div class="game-ui-resource-item" data-resource="stone">
                        <img src="${getImagePath('ui/icon_resource_stone.png')}" alt="Stone" class="game-ui-resource-icon" onerror="this.style.display='none'">
                        <div class="game-ui-resource-content">
                            <span class="game-ui-resource-value">${this.formatNumber(resources.stone || 0)}</span>
                            <span class="game-ui-resource-storage">/ ${this.formatNumber(storage.stone || 0)}</span>
                        </div>
                    </div>
                    <div class="game-ui-resource-item" data-resource="iron">
                        <img src="${getImagePath('ui/icon_resource_iron.png')}" alt="Iron" class="game-ui-resource-icon" onerror="this.style.display='none'">
                        <div class="game-ui-resource-content">
                            <span class="game-ui-resource-value">${this.formatNumber(resources.iron || 0)}</span>
                            <span class="game-ui-resource-storage">/ ${this.formatNumber(storage.iron || 0)}</span>
                        </div>
                    </div>
                    <div class="game-ui-resource-item" data-resource="food">
                        <img src="${getImagePath('ui/icon_resource_grain.png')}" alt="Food" class="game-ui-resource-icon" onerror="this.style.display='none'">
                        <div class="game-ui-resource-content">
                            <span class="game-ui-resource-value">${this.formatNumber(resources.food || 0)}</span>
                            <span class="game-ui-resource-storage">/ ${this.formatNumber(storage.food || 0)}</span>
                        </div>
                    </div>
                    <div class="game-ui-resource-item" data-resource="gold">
                        <img src="${getImagePath('ui/icon_resource_gold.png')}" alt="Gold" class="game-ui-resource-icon" onerror="this.style.display='none'">
                        <div class="game-ui-resource-content">
                            <span class="game-ui-resource-value">${this.formatNumber(resources.gold || 0)}</span>
                        </div>
                    </div>
                </div>
                
                <div class="game-ui-topbar-right">
                    <!-- Currency Display -->
                    <div class="game-ui-currency">
                        <div class="game-ui-currency-item" title="Free Currency (regenerates over time)">
                            <span class="game-ui-currency-label">⚡</span>
                            <span class="game-ui-currency-value" id="game-ui-free-currency">0</span>
                            <span class="game-ui-currency-max">/ 100</span>
                        </div>
                        <div class="game-ui-currency-item" title="Premium Currency">
                            <span class="game-ui-currency-label">💎</span>
                            <span class="game-ui-currency-value" id="game-ui-premium-currency">0</span>
                        </div>
                    </div>
                    
                    <div class="game-ui-controls">
                        <button class="game-ui-btn game-ui-btn-pause" id="game-ui-pause" title="Pause/Resume">
                            <span class="game-ui-btn-icon">⏸</span>
                        </button>
                        <div class="game-ui-speed-controls">
                            <button class="game-ui-speed-btn" data-speed="0.5" title="0.5x Speed">0.5x</button>
                            <button class="game-ui-speed-btn active" data-speed="1" title="1x Speed">1x</button>
                            <button class="game-ui-speed-btn" data-speed="2" title="2x Speed">2x</button>
                            <button class="game-ui-speed-btn" data-speed="4" title="4x Speed">4x</button>
                        </div>
                    </div>
                    <div class="game-ui-actions">
                        <button class="game-ui-btn" id="game-ui-world-map" title="World Map">🗺️</button>
                        <button class="game-ui-btn game-ui-btn-menu" id="game-ui-menu" title="Menu">☰</button>
                    </div>
                </div>
            </div>
            
            <!-- Main Game Area -->
            <div class="game-ui-main">
                <!-- Left Sidebar - Building Selection -->
                <div class="game-ui-sidebar game-ui-sidebar-left">
                    <div class="game-ui-sidebar-header">
                        <h3>Buildings</h3>
                    </div>
                    <div class="game-ui-building-categories">
                        <button class="game-ui-category-btn active" data-category="resources">Resources</button>
                        <button class="game-ui-category-btn" data-category="infrastructure">Infrastructure</button>
                        <button class="game-ui-category-btn" data-category="military">Military</button>
                        <button class="game-ui-category-btn" data-category="commerce">Commerce</button>
                    </div>
                    <div class="game-ui-building-list" id="game-ui-building-list">
                        <!-- Buildings will be populated here -->
                    </div>
                </div>
                
                <!-- Center - City Canvas -->
                <div class="game-ui-canvas-container">
                    <div class="game-ui-canvas-wrapper" id="game-ui-canvas-wrapper">
                        <!-- Canvas will be inserted here -->
                    </div>
                    <div class="game-ui-canvas-overlay">
                        <div class="game-ui-tile-info" id="game-ui-tile-info"></div>
                    </div>
                </div>
                
                <!-- Right Sidebar - Construction Queue & Info -->
                <div class="game-ui-sidebar game-ui-sidebar-right">
                    <div class="game-ui-sidebar-header">
                        <h3>Construction Queue</h3>
                        <span class="game-ui-queue-count" id="game-ui-queue-count">0</span>
                    </div>
                    <div class="game-ui-construction-queue" id="game-ui-construction-queue">
                        <!-- Construction queue items will be populated here -->
                    </div>
                    
                    <div class="game-ui-sidebar-section">
                        <div class="game-ui-sidebar-header">
                            <h3>City Stats</h3>
                        </div>
                        <div class="game-ui-city-stats" id="game-ui-city-stats">
                            <!-- City stats will be populated here -->
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Bottom Bar - Quick Actions -->
            <div class="game-ui-bottombar">
                <div class="game-ui-quick-actions">
                    <button class="game-ui-quick-btn" id="game-ui-quick-build" title="Build">
                        <span class="game-ui-quick-btn-icon">🔨</span>
                        <span class="game-ui-quick-btn-label">Build</span>
                    </button>
                    <button class="game-ui-quick-btn" id="game-ui-quick-research" title="Research">
                        <span class="game-ui-quick-btn-icon">📚</span>
                        <span class="game-ui-quick-btn-label">Research</span>
                    </button>
                    <button class="game-ui-quick-btn" id="game-ui-quick-troops" title="Troops">
                        <span class="game-ui-quick-btn-icon">⚔️</span>
                        <span class="game-ui-quick-btn-label">Troops</span>
                    </button>
                    <button class="game-ui-quick-btn" id="game-ui-quick-world" title="World Map">
                        <span class="game-ui-quick-btn-icon">🗺️</span>
                        <span class="game-ui-quick-btn-label">World</span>
                    </button>
                </div>
                <div class="game-ui-production-summary" id="game-ui-production-summary">
                    <!-- Production summary will be populated here -->
                </div>
            </div>
            
            <!-- Notification Container -->
            <div class="game-ui-notifications" id="game-ui-notifications"></div>
        `;
        
        // Populate building list
        this.renderBuildingList();
        this.renderConstructionQueue();
        this.renderCityStats();
        this.renderProductionSummary();
    }
    
    /**
     * Render building list based on selected category
     */
    renderBuildingList() {
        const category = this.selectedCategory || 'resources';
        const listContainer = this.container.querySelector('#game-ui-building-list');
        if (!listContainer) return;
        
        const buildings = this.getBuildingsByCategory(category);
        
        listContainer.innerHTML = buildings.map(building => `
            <div class="game-ui-building-item ${this.selectedBuilding === building.key ? 'selected' : ''}" 
                 data-building="${building.key}"
                 title="${building.name}">
                <img src="${getImagePath(`buildings/${building.image}`)}" 
                     alt="${building.name}" 
                     class="game-ui-building-icon"
                     onerror="this.style.display='none'">
                <div class="game-ui-building-info">
                    <div class="game-ui-building-name">${building.name}</div>
                    <div class="game-ui-building-cost">${this.formatBuildingCost(building.cost)}</div>
                </div>
            </div>
        `).join('');
    }
    
    /**
     * Get buildings by category
     */
    getBuildingsByCategory(category) {
        const buildingMap = {
            resources: [
                { key: 'forester', name: 'Forester', image: 'forester-v6-64.png', cost: { wood: 50, stone: 0, iron: 0, food: 0, gold: 0 } },
                { key: 'sawmill', name: 'Sawmill', image: 'sawmill-64.png', cost: { wood: 100, stone: 50, iron: 0, food: 0, gold: 0 } },
                { key: 'quarry', name: 'Quarry', image: 'quarry-2-64.png', cost: { wood: 50, stone: 0, iron: 0, food: 0, gold: 0 } },
                { key: 'stonemason', name: 'Stonemason', image: 'stonemasonery-64.png', cost: { wood: 100, stone: 50, iron: 0, food: 0, gold: 0 } },
                { key: 'mine', name: 'Iron Mine', image: 'iron-mine-64.png', cost: { wood: 100, stone: 100, iron: 0, food: 0, gold: 0 } },
                { key: 'foundry', name: 'Foundry', image: 'smelter-64.png', cost: { wood: 150, stone: 100, iron: 50, food: 0, gold: 0 } },
                { key: 'farm', name: 'Farm', image: 'farm-v3-64.png', cost: { wood: 50, stone: 0, iron: 0, food: 0, gold: 0 } },
                { key: 'mill', name: 'Mill', image: 'grain-mill-64-fix.png', cost: { wood: 100, stone: 50, iron: 0, food: 0, gold: 0 } }
            ],
            infrastructure: [
                { key: 'casa', name: 'Casa', image: 'basic_house-64.png', cost: { wood: 30, stone: 0, iron: 0, food: 0, gold: 0 } },
                { key: 'cellarium', name: 'Cellarium', image: 'warehouse-v2-64.png', cost: { wood: 100, stone: 50, iron: 0, food: 0, gold: 0 } },
                { key: 'cache', name: 'Cache', image: 'hideaway-64.png', cost: { wood: 150, stone: 100, iron: 0, food: 0, gold: 0 } },
                { key: 'townhall', name: 'Basilica', image: 'basilica-fix-64.png', cost: { wood: 200, stone: 200, iron: 100, food: 0, gold: 0 } }
            ],
            military: [
                { key: 'barracks', name: 'Barracks', image: 'barracks-v2-64.png', cost: { wood: 150, stone: 100, iron: 50, food: 0, gold: 0 } },
                { key: 'castle', name: 'Castle', image: 'castle-64-fix.png', cost: { wood: 300, stone: 300, iron: 150, food: 0, gold: 0 } },
                { key: 'guard_house', name: 'Guard House', image: 'guard\'s_house-v2-64.png', cost: { wood: 100, stone: 50, iron: 0, food: 0, gold: 0 } },
                { key: 'training_ground', name: 'Training Ground', image: 'training_arena-64.png', cost: { wood: 200, stone: 150, iron: 100, food: 0, gold: 0 } },
                { key: 'stable', name: 'Stable', image: 'stable-v2-fix-64.png', cost: { wood: 150, stone: 100, iron: 50, food: 0, gold: 0 } },
                { key: 'shipyard', name: 'Shipyard', image: 'shipyard-front-flip-64.png', cost: { wood: 200, stone: 150, iron: 100, food: 0, gold: 0 } }
            ],
            commerce: [
                { key: 'manor', name: 'Manor', image: 'villa-64.png', cost: { wood: 200, stone: 150, iron: 0, food: 0, gold: 0 } },
                { key: 'bazaar', name: 'Bazaar', image: 'market-64.png', cost: { wood: 150, stone: 100, iron: 0, food: 0, gold: 0 } },
                { key: 'wharf', name: 'Wharf', image: 'ports-front-flip-64.png', cost: { wood: 200, stone: 150, iron: 100, food: 0, gold: 0 } }
            ]
        };
        
        return buildingMap[category] || [];
    }
    
    /**
     * Format building cost for display
     */
    formatBuildingCost(cost) {
        const parts = [];
        if (cost.wood > 0) parts.push(`🪵 ${cost.wood}`);
        if (cost.stone > 0) parts.push(`🪨 ${cost.stone}`);
        if (cost.iron > 0) parts.push(`⚒️ ${cost.iron}`);
        if (cost.food > 0) parts.push(`🌾 ${cost.food}`);
        if (cost.gold > 0) parts.push(`🪙 ${cost.gold}`);
        return parts.join(' ');
    }
    
    /**
     * Render construction queue
     */
    renderConstructionQueue() {
        const queueContainer = this.container.querySelector('#game-ui-construction-queue');
        const countContainer = this.container.querySelector('#game-ui-queue-count');
        if (!queueContainer) return;
        
        const queue = this.gameState?.constructionManager?.queue || [];
        const count = queue.length;
        
        if (countContainer) {
            countContainer.textContent = count;
        }
        
        if (queue.length === 0) {
            queueContainer.innerHTML = '<div class="game-ui-queue-empty">No buildings in queue</div>';
            return;
        }
        
        const currencyManager = this.gameState?.currencyManager;
        const currencies = currencyManager ? currencyManager.getCurrencies() : { free: 0, freeMax: 100, premium: 0 };
        
        queueContainer.innerHTML = queue.map(item => {
            const progress = Math.min(100, ((Date.now() - item.startedAt) / (item.buildTime * 1000)) * 100);
            const remaining = Math.max(0, item.buildTime - Math.floor((Date.now() - item.startedAt) / 1000));
            
            const canSpeedUp = currencyManager && (currencyManager.canAfford(5, 'free') || currencyManager.canAfford(10, 'premium'));
            const canInstantComplete = currencyManager && (currencyManager.canAfford(20, 'free') || currencyManager.canAfford(50, 'premium'));
            
            return `
                <div class="game-ui-queue-item" data-queue-id="${item.id}">
                    <div class="game-ui-queue-item-header">
                        <span class="game-ui-queue-item-name">${item.buildingType} (L${item.level})</span>
                        <div class="game-ui-queue-actions">
                            <button class="game-ui-queue-speedup ${!canSpeedUp ? 'disabled' : ''}" 
                                    data-queue-id="${item.id}" 
                                    title="Speed Up (5⚡ or 10💎)"
                                    ${!canSpeedUp ? 'disabled' : ''}>⚡</button>
                            <button class="game-ui-queue-instant ${!canInstantComplete ? 'disabled' : ''}" 
                                    data-queue-id="${item.id}" 
                                    title="Instant Complete (20⚡ or 50💎)"
                                    ${!canInstantComplete ? 'disabled' : ''}>✨</button>
                            <button class="game-ui-queue-cancel" data-queue-id="${item.id}" title="Cancel">×</button>
                        </div>
                    </div>
                    <div class="game-ui-queue-progress">
                        <div class="game-ui-queue-progress-bar" style="width: ${progress}%"></div>
                    </div>
                    <div class="game-ui-queue-time">${this.formatTime(remaining)} remaining</div>
                </div>
            `;
        }).join('');
    }
    
    /**
     * Render city stats
     */
    renderCityStats() {
        const statsContainer = this.container.querySelector('#game-ui-city-stats');
        if (!statsContainer || !this.gameEngine) return;
        
        const production = {
            wood: this.gameEngine.wood_tot || 0,
            stone: this.gameEngine.stone_tot || 0,
            iron: this.gameEngine.iron_tot || 0,
            food: this.gameEngine.food_tot || 0,
            gold: this.gameEngine.gold_tot || 0
        };
        
        statsContainer.innerHTML = `
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">Production/Hour:</span>
            </div>
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">🪵 Wood:</span>
                <span class="game-ui-stat-value">${this.formatNumber(production.wood)}</span>
            </div>
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">🪨 Stone:</span>
                <span class="game-ui-stat-value">${this.formatNumber(production.stone)}</span>
            </div>
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">⚒️ Iron:</span>
                <span class="game-ui-stat-value">${this.formatNumber(production.iron)}</span>
            </div>
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">🌾 Food:</span>
                <span class="game-ui-stat-value">${this.formatNumber(production.food)}</span>
            </div>
            <div class="game-ui-stat-row">
                <span class="game-ui-stat-label">🪙 Gold:</span>
                <span class="game-ui-stat-value">${this.formatNumber(production.gold)}</span>
            </div>
        `;
    }
    
    /**
     * Render production summary
     */
    renderProductionSummary() {
        const summaryContainer = this.container.querySelector('#game-ui-production-summary');
        if (!summaryContainer || !this.gameEngine) return;
        
        const totalProduction = (this.gameEngine.wood_tot || 0) + 
                               (this.gameEngine.stone_tot || 0) + 
                               (this.gameEngine.iron_tot || 0) + 
                               (this.gameEngine.food_tot || 0) + 
                               (this.gameEngine.gold_tot || 0);
        
        summaryContainer.innerHTML = `
            <div class="game-ui-production-total">
                <span class="game-ui-production-label">Total Production:</span>
                <span class="game-ui-production-value">${this.formatNumber(totalProduction)}/hr</span>
            </div>
        `;
    }
    
    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Category buttons
        const categoryBtns = this.container.querySelectorAll('.game-ui-category-btn');
        categoryBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                categoryBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.selectedCategory = btn.dataset.category;
                this.renderBuildingList();
            });
        });
        
        // Building selection
        this.container.addEventListener('click', (e) => {
            const buildingItem = e.target.closest('.game-ui-building-item');
            if (buildingItem) {
                const buildingKey = buildingItem.dataset.building;
                this.selectBuilding(buildingKey);
            }
            
            // Queue cancel
            const cancelBtn = e.target.closest('.game-ui-queue-cancel');
            if (cancelBtn) {
                const queueId = parseInt(cancelBtn.dataset.queueId);
                if (this.callbacks.onCancelConstruction) {
                    this.callbacks.onCancelConstruction(queueId);
                }
            }
            
            // Speed up
            const speedUpBtn = e.target.closest('.game-ui-queue-speedup');
            if (speedUpBtn && !speedUpBtn.disabled) {
                const queueId = parseInt(speedUpBtn.dataset.queueId);
                if (this.callbacks.onSpeedUpConstruction) {
                    this.callbacks.onSpeedUpConstruction(queueId);
                }
            }
            
            // Instant complete
            const instantBtn = e.target.closest('.game-ui-queue-instant');
            if (instantBtn && !instantBtn.disabled) {
                const queueId = parseInt(instantBtn.dataset.queueId);
                if (this.callbacks.onInstantComplete) {
                    this.callbacks.onInstantComplete(queueId);
                }
            }
        });
        
        // Pause button
        const pauseBtn = this.container.querySelector('#game-ui-pause');
        if (pauseBtn) {
            pauseBtn.addEventListener('click', () => {
                if (this.callbacks.onTogglePause) {
                    this.callbacks.onTogglePause();
                }
            });
        }
        
        // Speed buttons
        const speedBtns = this.container.querySelectorAll('.game-ui-speed-btn');
        speedBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const speed = parseFloat(btn.dataset.speed);
                speedBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                if (this.callbacks.onSetSpeed) {
                    this.callbacks.onSetSpeed(speed);
                }
            });
        });
    }
    
    /**
     * Select a building
     */
    selectBuilding(buildingKey) {
        this.selectedBuilding = buildingKey;
        this.renderBuildingList();
        
        if (this.callbacks.onBuildingSelected) {
            this.callbacks.onBuildingSelected(buildingKey);
        }
    }
    
    /**
     * Start auto-update
     */
    startAutoUpdate() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        
        this.updateInterval = setInterval(() => {
            this.update();
        }, 1000);
    }
    
    /**
     * Stop auto-update
     */
    stopAutoUpdate() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }
    
    /**
     * Update the UI
     */
    update() {
        const resources = this.gameState?.getResources() || {};
        const storage = this.gameState?.getStorage() || {};
        const isPaused = this.gameState?.isPaused || false;
        
        // Update currency display
        const currencyManager = this.gameState?.currencyManager;
        if (currencyManager) {
            const currencies = currencyManager.getCurrencies();
            const freeCurrencyElem = this.container.querySelector('#game-ui-free-currency');
            const premiumCurrencyElem = this.container.querySelector('#game-ui-premium-currency');
            
            if (freeCurrencyElem) {
                freeCurrencyElem.textContent = currencies.free || 0;
            }
            if (premiumCurrencyElem) {
                premiumCurrencyElem.textContent = currencies.premium || 0;
            }
        }
        
        // Update resource values
        const resourceItems = this.container.querySelectorAll('.game-ui-resource-item');
        resourceItems.forEach(item => {
            const resource = item.dataset.resource;
            const valueEl = item.querySelector('.game-ui-resource-value');
            const storageEl = item.querySelector('.game-ui-resource-storage');
            
            if (valueEl) {
                valueEl.textContent = this.formatNumber(resources[resource] || 0);
            }
            if (storageEl && storage[resource] !== undefined) {
                storageEl.textContent = `/ ${this.formatNumber(storage[resource] || 0)}`;
            }
        });
        
        // Update pause button
        const pauseBtn = this.container.querySelector('#game-ui-pause');
        if (pauseBtn) {
            pauseBtn.querySelector('.game-ui-btn-icon').textContent = isPaused ? '▶' : '⏸';
        }
        
        // Update construction queue
        this.renderConstructionQueue();
        this.renderCityStats();
    }
    
    /**
     * Format number for display
     */
    formatNumber(num) {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(2) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        } else {
            return Math.floor(num).toString();
        }
    }
    
    /**
     * Format time for display
     */
    formatTime(seconds) {
        if (seconds < 60) {
            return `${seconds}s`;
        } else if (seconds < 3600) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}m ${secs}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${mins}m`;
        }
    }
    
    /**
     * Get canvas wrapper element
     */
    getCanvasWrapper() {
        return this.container.querySelector('#game-ui-canvas-wrapper');
    }
    
    /**
     * Cleanup
     */
    destroy() {
        this.stopAutoUpdate();
    }
}

